import Breadcrumb from '../../../components/ui/Breadcrumb';
/**
 * ClinicalDashboardPage Component
 *
 * Main clinical quality metrics dashboard page.
 * Read-only view displaying:
 * - SOAP completion percentage (average across recent consultations)
 * - ROS coverage metrics
 * - Inactive patients list (90+ days without visit)
 *
 * Access: Doctors see their own metrics; Admin sees org-wide metrics.
 * All data is read-only - no modification allowed per spec requirements.
 *
 * @example
 * ```tsx
 * <ClinicalDashboardPage />
 * ```
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { useClinicalDashboard } from '../hooks/useClinicalDashboard';
import RosCompletenessCard from '../components/RosCompletenessCard';
import InactivePatientsList from '../components/InactivePatientsList';

const ClinicalDashboardPage: React.FC = () => {
  const {
    summary,
    inactivePatients,
    selectedPatientId,
    patientRos,
    loading,
    error,
    fetchAll,
    fetchPatientRos,
    clearSelectedPatient,
  } = useClinicalDashboard();

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handlePatientSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const patientId = parseInt(e.target.value, 10);
    if (patientId > 0) {
      fetchPatientRos(patientId);
    } else {
      clearSelectedPatient();
    }
  };

  /**
   * Returns appropriate badge for SOAP completion status
   */
  const getSoapStatusBadge = (targetMet: boolean, percentage: number) => {
    if (targetMet) {
      return <span className="badge bg-success">Objetivo Alcanzado (&gt;80%)</span>;
    }
    if (percentage <= 25) {
      return <span className="badge bg-danger">Crítico (&lt;25%)</span>;
    }
    return <span className="badge bg-warning text-dark">Bajo el Objetivo (&lt;80%)</span>;
  };

  /**
   * Returns color class for SOAP percentage display
   */
  const getSoapColorClass = (percentage: number, targetMet: boolean): string => {
    if (targetMet) return 'text-success';
    if (percentage <= 25) return 'text-danger';
    return 'text-warning';
  };

  return (
    <>
      {/* Breadcrumb */}
      <div className="page-header">
        <div className="row">
          <div className="col-sm-12">
            <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Métricas de Calidad Clínica' }]} />
          </div>
        </div>
      </div>

      {/* Read-Only Indicator Banner */}
      <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
        <FeatherIcon icon="lock" className="me-2" style={{ width: 18, height: 18 }} />
        <div>
          <strong>Panel de Solo Lectura</strong> — Este panel muestra métricas clínicas únicamente.
          No es posible modificar datos desde aquí.
        </div>
      </div>

      {/* Page Title */}
      <div className="row mb-4">
        <div className="col-md-8">
          <h3 className="page-title">Indicadores de Calidad Clínica</h3>
          <p className="text-muted">
            Métricas de calidad derivadad de registros de consulta existentes
          </p>
        </div>
        <div className="col-md-4 text-end">
          <small className="text-muted">
            Período: {summary?.period?.from || '-'} a {summary?.period?.to || '-'}
          </small>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <FeatherIcon icon="alert-circle" className="me-2" />
          <div>{error}</div>
        </div>
      )}

      {/* Loading State */}
      {loading && !summary && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted mt-2">Cargando métricas clínicas...</p>
        </div>
      )}

      {/* Dashboard Content */}
      {summary && (
        <>
          {/* SOAP Completion Card */}
          <div className="row">
            <div className="col-md-6 col-lg-4">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Completitud SOAP</h4>
                  {getSoapStatusBadge(summary.soap_completion.target_met, summary.soap_completion.average_percentage)}
                </div>
                <div className="card-body text-center">
                  <h2
                    className={`display-4 fw-bold ${getSoapColorClass(
                      summary.soap_completion.average_percentage,
                      summary.soap_completion.target_met
                    )}`}
                  >
                    {summary.soap_completion.average_percentage.toFixed(1)}%
                  </h2>
                  <p className="text-muted mb-1">
                    Promedio de completitud en últimas consultas
                  </p>
                  <div className="progress mt-3" style={{ height: '10px' }}>
                    <div
                      className={`progress-bar ${
                        summary.soap_completion.target_met ? 'bg-success' : 'bg-warning'
                      }`}
                      role="progressbar"
                      style={{ width: `${Math.min(summary.soap_completion.average_percentage, 100)}%` }}
                      aria-valuenow={summary.soap_completion.average_percentage}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Objetivo: &gt;80% | Consultas analizadas: {summary.soap_completion.consultations_analyzed}
                  </small>
                </div>
              </div>
            </div>

            {/* ROS Coverage Card */}
            <div className="col-md-6 col-lg-4">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Cobertura ROS</h4>
                  {summary.ros_coverage.total_consultations > 0 && (
                    <span className="badge bg-primary float-end">
                      {Math.round(
                        (summary.ros_coverage.consultations_with_ros /
                          summary.ros_coverage.total_consultations) *
                          100
                      )}
                      % cobertura
                    </span>
                  )}
                </div>
                <div className="card-body text-center">
                  <h2 className="display-4 fw-bold text-primary">
                    {summary.ros_coverage.consultations_with_ros}
                    <span className="h4 text-muted fw-normal">
                      /{summary.ros_coverage.total_consultations}
                    </span>
                  </h2>
                  <p className="text-muted mb-1">
                    Consultas con datos de Revisión de Sistemas
                  </p>
                  <div className="progress mt-3" style={{ height: '10px' }}>
                    <div
                      className="progress-bar bg-primary"
                      role="progressbar"
                      style={{
                        width: `${
                          summary.ros_coverage.total_consultations > 0
                            ? (summary.ros_coverage.consultations_with_ros /
                                summary.ros_coverage.total_consultations) *
                              100
                            : 0
                        }%`,
                      }}
                      aria-valuenow={summary.ros_coverage.consultations_with_ros}
                      aria-valuemin={0}
                      aria-valuemax={summary.ros_coverage.total_consultations}
                    />
                  </div>
                  <small className="text-muted mt-2 d-block">
                    14 sistemas orgánicos por consulta
                  </small>
                </div>
              </div>
            </div>

            {/* Inactive Patients Summary Card */}
            <div className="col-md-12 col-lg-4">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Pacientes Inactivos</h4>
                  <span className="badge bg-warning text-dark float-end">
                    {summary.inactive_patients.threshold_days}+ días
                  </span>
                </div>
                <div className="card-body text-center">
                  <h2
                    className={`display-4 fw-bold ${
                      summary.inactive_patients.count > 0 ? 'text-danger' : 'text-success'
                    }`}
                  >
                    {summary.inactive_patients.count}
                  </h2>
                  <p className="text-muted mb-1">
                    Pacientes sin visita en {summary.inactive_patients.threshold_days}+ días
                  </p>
                  {summary.inactive_patients.count > 0 && (
                    <Link
                      to="#inactive-patients"
                      className="btn btn-outline-danger btn-sm mt-2"
                    >
                      <FeatherIcon icon="eye" className="me-1" style={{ width: 14, height: 14 }} />
                      Ver lista
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Inactive Patients List Section */}
          <div className="row mt-4" id="inactive-patients">
            <div className="col-12">
              <InactivePatientsList
                patients={inactivePatients}
                thresholdDays={summary.inactive_patients.threshold_days}
                loading={loading}
              />
            </div>
          </div>

          {/* Patient ROS Selector */}
          <div className="row mt-4">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title d-inline-block">Revisión de Sistemas por Paciente</h4>
                </div>
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <label htmlFor="patient-ros-select" className="form-label">
                        Seleccionar paciente para ver su completitud ROS:
                      </label>
                      <select
                        id="patient-ros-select"
                        className="form-select"
                        value={selectedPatientId ?? ''}
                        onChange={handlePatientSelect}
                      >
                        <option value="">-- Seleccionar paciente --</option>
                        {inactivePatients.map((patient) => (
                          <option key={patient.id} value={patient.id}>
                            {patient.last_name}, {patient.first_name} ({patient.medical_record_number})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Per-Patient ROS Completeness Card */}
                  {selectedPatientId && patientRos && (
                    <div className="row mt-4">
                      <div className="col-md-6">
                        <RosCompletenessCard
                          documented={patientRos.documented_count}
                          total={patientRos.total}
                          percentage={patientRos.percentage}
                          isComplete={patientRos.documented_count === patientRos.total}
                          title={`ROS - ${inactivePatients.find((p) => p.id === selectedPatientId)?.first_name} ${inactivePatients.find((p) => p.id === selectedPatientId)?.last_name}`}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-header">
                            <h4 className="card-title d-inline-block">Detalle por Sistema</h4>
                          </div>
                          <div className="card-body">
                            <ul className="list-unstyled mb-0">
                              {patientRos.systems.map((system) => (
                                <li key={system.code} className="d-flex align-items-center mb-2">
                                  {system.documented ? (
                                    <FeatherIcon
                                      icon="check-circle"
                                      className="text-success me-2"
                                      style={{ width: 16, height: 16 }}
                                    />
                                  ) : (
                                    <FeatherIcon
                                      icon="circle"
                                      className="text-muted me-2"
                                      style={{ width: 16, height: 16 }}
                                    />
                                  )}
                                  <span className={system.documented ? '' : 'text-muted'}>
                                    {system.label}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedPatientId && !patientRos && !error && (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      <p className="text-muted mt-2">Cargando datos ROS del paciente...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Empty State - No Data */}
      {!loading && !summary && !error && (
        <div className="text-center py-5">
          <FeatherIcon
            icon="activity"
            className="text-muted mb-3"
            style={{ width: 64, height: 64, opacity: 0.3 }}
          />
          <h5 className="text-muted">Sin datos disponibles</h5>
          <p className="text-muted">
            No hay métricas clínicas para mostrar en este momento.
          </p>
        </div>
      )}
    </>
  );
};

export default ClinicalDashboardPage;